import jStat from 'jstat';

export interface QuartileResult {
  quartiles: {
    q1: number;  // First quartile (25th percentile)
    q2: number;  // Second quartile (median, 50th percentile)
    q3: number;  // Third quartile (75th percentile)
  };
  iqr: number;  // Interquartile range (Q3 - Q1)
  data: number[];
  interpretation: string;
}

export function calculateQuartiles(data: number[]): QuartileResult {
  // Handle empty array or invalid data
  if (!data || data.length === 0) {
    return {
      quartiles: {
        q1: 0,
        q2: 0,
        q3: 0
      },
      iqr: 0,
      data: [],
      interpretation: 'No data available for quartile calculation.'
    };
  }

  // Handle single value
  if (data.length === 1) {
    // Since we know data has length 1, data[0] must exist
    const value = data[0]!;
    return {
      quartiles: {
        q1: value,
        q2: value,
        q3: value
      },
      iqr: 0,
      data: [value],
      interpretation: `Single value dataset: all quartiles equal to ${value}.`
    };
  }

  const sortedData = [...data].sort((a, b) => a - b);
  const quartileArray = jStat.quartiles(sortedData);
  
  // Since jStat.quartiles always returns [q1, q2, q3] for non-empty arrays
  const q1 = quartileArray[0]!;
  const q2 = quartileArray[1]!;
  const q3 = quartileArray[2]!;
  const iqr = q3 - q1;
  
  return {
    quartiles: {
      q1,
      q2,
      q3
    },
    iqr,
    data: sortedData,
    interpretation: `The data is divided into four parts: 25% below Q1 (${q1.toFixed(2)}), 25% between Q1 and Q2, 25% between Q2 and Q3, and 25% above Q3 (${q3.toFixed(2)}). The IQR is ${iqr.toFixed(2)}.`
  };
} 